// ============================================================================
// Vertrags-Templates — lokale Entwürfe (kein neuer Microservice)
// ----------------------------------------------------------------------------
// Persistenz-IST: Browser-localStorage, namespaced pro Portal-customerId.
// PocketBase hat keine Collection für Vertrags-Templates — bewusst nicht
// erfunden. Kanzlei-Muster von Phillip kommen später in dieselben Slots.
// Keine Mandanten-PII ins Repo; Werte bleiben im Browser des Users.
// ============================================================================

export interface EigeneKlausel {
  id: string;
  titel: string;
  text: string;
}

/** UI-Name „Individuell“; Speicherform bleibt eigeneKlauseln (alte Drafts). */
export type IndividuellBlock = EigeneKlausel;

export const MAX_INDIVIDUELL_BLOCKS = 40;
export const INDIVIDUELL_TITEL_MAX = 200;
export const INDIVIDUELL_TEXT_MAX = 8000;

export interface SavedContractDraft {
  id: string;
  name: string;
  templateId: string;
  form: Record<string, unknown>;
  enabledBlocks: string[];
  extraValues: Record<string, unknown>;
  eigeneKlauseln: EigeneKlausel[];
  savedAt: string;
}

export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

const KEY_PREFIX = 'arkorbit.portal.contract-templates.v1:';
const MAX_DRAFTS = 40;

export function contractDraftStorageKey(customerId: string): string {
  const id = String(customerId || '').trim() || 'anon';
  return `${KEY_PREFIX}${id}`;
}

export function newEigeneKlausel(): EigeneKlausel {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `k-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return { id, titel: '', text: '' };
}

export function newIndividuellBlock(): IndividuellBlock {
  return newEigeneKlausel();
}

function clip(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max);
}

export function normalizeIndividuellBlocks(raw: unknown): IndividuellBlock[] {
  if (!Array.isArray(raw)) return [];
  const out: IndividuellBlock[] = [];
  for (const item of raw) {
    if (out.length >= MAX_INDIVIDUELL_BLOCKS) break;
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const o = item as Record<string, unknown>;
    const id = typeof o.id === 'string' && o.id.trim() ? o.id.trim() : `k-${out.length + 1}`;
    const titel = typeof o.titel === 'string' ? clip(o.titel, INDIVIDUELL_TITEL_MAX) : '';
    const text = typeof o.text === 'string' ? clip(o.text, INDIVIDUELL_TEXT_MAX) : '';
    out.push({ id, titel, text });
  }
  return out;
}

/**
 * Polar CONTRACT_GERUEST: nur User-Text in die Ausgabe.
 * Leere Blöcke entfallen; kein Stub, kein §-Normtext, keine id.
 */
export function individuellOutputItems(
  blocks: IndividuellBlock[] | unknown,
): Array<{ titel: string; text: string }> {
  const list = normalizeIndividuellBlocks(blocks);
  const out: Array<{ titel: string; text: string }> = [];
  for (const k of list) {
    const titel = k.titel.trim();
    const text = k.text.trim();
    if (!titel && !text) continue;
    out.push({
      titel: titel || `Individuell ${out.length + 1}`,
      text,
    });
  }
  return out;
}

function parseDrafts(raw: string | null): SavedContractDraft[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isDraftShape);
  } catch {
    return [];
  }
}

function isDraftShape(x: unknown): x is SavedContractDraft {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.name === 'string' &&
    typeof o.templateId === 'string' &&
    typeof o.savedAt === 'string' &&
    o.form !== null &&
    typeof o.form === 'object' &&
    Array.isArray(o.enabledBlocks) &&
    Array.isArray(o.eigeneKlauseln)
  );
}

export function loadContractDrafts(
  customerId: string,
  storage?: DraftStorage | null,
): SavedContractDraft[] {
  const store = storage ?? (typeof localStorage === 'undefined' ? null : localStorage);
  if (!store) return [];
  return parseDrafts(store.getItem(contractDraftStorageKey(customerId)));
}

export function saveContractDraft(
  customerId: string,
  draft: Omit<SavedContractDraft, 'savedAt'> & { savedAt?: string },
  storage?: DraftStorage | null,
): SavedContractDraft {
  const store = storage ?? (typeof localStorage === 'undefined' ? null : localStorage);
  if (!store) {
    throw new Error('localStorage ist in dieser Umgebung nicht verfügbar.');
  }
  const saved: SavedContractDraft = {
    id: draft.id,
    name: draft.name.trim() || 'Unbenanntes Template',
    templateId: draft.templateId,
    form: draft.form && typeof draft.form === 'object' ? draft.form : {},
    enabledBlocks: Array.isArray(draft.enabledBlocks) ? draft.enabledBlocks : [],
    extraValues: draft.extraValues && typeof draft.extraValues === 'object' ? draft.extraValues : {},
    eigeneKlauseln: normalizeIndividuellBlocks(draft.eigeneKlauseln),
    savedAt: draft.savedAt || new Date().toISOString(),
  };
  const all = loadContractDrafts(customerId, store).filter((d) => d.id !== saved.id);
  all.unshift(saved);
  store.setItem(contractDraftStorageKey(customerId), JSON.stringify(all.slice(0, MAX_DRAFTS)));
  return saved;
}

export function deleteContractDraft(
  customerId: string,
  draftId: string,
  storage?: DraftStorage | null,
): void {
  const store = storage ?? (typeof localStorage === 'undefined' ? null : localStorage);
  if (!store) return;
  const next = loadContractDrafts(customerId, store).filter((d) => d.id !== draftId);
  store.setItem(contractDraftStorageKey(customerId), JSON.stringify(next));
}
